import { MentionNode } from './MentionPlugin/MentionNode';
import { TicketNode } from './TicketPlugin/TicketNode';
import { PlaceholderTicketNode } from './TicketPlugin/PlaceholderTicketNode';
import { cn } from '@/lib/utils';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { LinkNode } from '@lexical/link';
import { ListNode, ListItemNode } from '@lexical/list';
import { InitialConfigType } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ParagraphNode, TextNode } from 'lexical';
import { useMemo } from 'react';

export const useEditorConfig = (namespace: string, readonly = false) =>
  useMemo<InitialConfigType>(
    () => ({
      editable: !readonly,
      namespace,
      nodes: [
        ParagraphNode,
        TextNode,
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        LinkNode,
        CodeNode,
        CodeHighlightNode,
        MentionNode,
        TicketNode,
        PlaceholderTicketNode,
      ],
      onError(error: Error) {
        throw error;
      },
      theme: {
        paragraph: 'mb-2',
        heading: {
          h1: 'text-3xl font-bold mb-4',
          h2: 'text-2xl font-bold mb-3',
          h3: 'text-xl font-bold mb-2',
          h4: 'text-lg font-bold mb-2',
          h5: 'text-base font-bold mb-2',
          h6: 'text-sm font-bold mb-2',
        },
        list: {
          checklist: 'list-none',
          nested: {
            listitem: 'list-none',
          },
          ol: 'list-decimal ml-4',
          ul: 'list-disc ml-4',
          listitem: 'mb-1',
          listitemChecked: cn(
            `relative mx-2 block min-h-[1.5em] list-none px-6 line-through outline-none before:absolute before:top-1/2 before:left-0 before:block before:h-[0.9em] before:w-[0.9em] before:-translate-y-1/2 before:cursor-pointer before:rounded-xs before:border before:border-blue-500 before:bg-blue-500 before:bg-cover before:content-["\\200B"] after:absolute after:top-[45%] after:left-[0.35em] after:block after:h-[0.4em] after:w-[0.2em] after:-translate-y-1/2 after:rotate-45 after:cursor-pointer after:border-r-[0.1em] after:border-b-[0.1em] after:border-solid after:border-white after:content-[""] focus:before:rounded-xs focus:before:shadow-[0_0_0_2px_#a6cdfe]`,
          ),
          listitemUnchecked: cn(
            'before:border-muted-foreground relative mx-2 block min-h-[1.5em] list-none px-6 outline-none before:absolute before:top-1/2 before:left-0 before:block before:h-[0.9em] before:w-[0.9em] before:-translate-y-1/2 before:cursor-pointer before:rounded-xs before:border before:bg-cover before:content-["\\200B"] focus:before:rounded-xs focus:before:shadow-[0_0_0_2px_#a6cdfe]',
          ),
        },
        link: 'text-blue-600 hover:text-blue-800 underline',
        quote: 'border-l-4 border-border pl-4 italic text-muted-foreground',
        text: {
          bold: 'font-bold',
          italic: 'italic',
          strikethrough: 'line-through',
          code: 'bg-muted px-1 py-0.5 rounded text-sm font-mono',
        },
      },
    }),
    [namespace, readonly],
  );
